import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { hasUnsavedWork } from "@/hooks/useUnsavedWork";

const UPDATE_TOAST_ID = "dallali-new-version";

/**
 * Whether the user is mid-edit right now — a conservative, form-agnostic guard
 * so a refocus auto-reload never eats text someone is typing. Combined with the
 * {@link hasUnsavedWork} registry for forms that opt in explicitly.
 */
function isEditingNow(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable === true;
}

function isUnsafeToReload(): boolean {
  return hasUnsavedWork() || isEditingNow();
}

/**
 * Mounts the new-deployment detector once at app level. Shows a brief bilingual
 * courtesy toast when a new build is live, then auto-refreshes the stale tab at
 * the next safe moment — a route change or a tab refocus — never over unsaved
 * work. No new service worker; app-level polling only.
 */
export function VersionGate() {
  const { t } = useLanguage();
  const location = useLocation();
  const { updateAvailable, reloadNow, reloadIfSafe } = useVersionCheck({
    isUnsafe: isUnsafeToReload,
  });

  const toastShownRef = useRef(false);
  const prevPathRef = useRef(location.pathname);

  // Announce the new version once (idempotent via fixed toast id). The reload
  // still happens automatically at the next safe point; the button just lets an
  // impatient user trigger it immediately.
  useEffect(() => {
    if (!updateAvailable || toastShownRef.current) return;
    toastShownRef.current = true;
    toast(
      t(
        "نسخة جديدة من دلّالي — يتم التحديث…",
        "New version of Dallali — refreshing…",
      ),
      {
        id: UPDATE_TOAST_ID,
        duration: Infinity,
        action: {
          label: t("تحديث الآن", "Refresh now"),
          onClick: reloadNow,
        },
      },
    );
  }, [updateAvailable, t, reloadNow]);

  // A route change is an inherently safe moment: the user navigated away from
  // wherever they were, so reloading to the new build loses nothing.
  useEffect(() => {
    if (updateAvailable && location.pathname !== prevPathRef.current) {
      reloadIfSafe();
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, updateAvailable, reloadIfSafe]);

  return null;
}
